import { Component, computed, inject, input, output } from '@angular/core';
import { PhoneConsentsModalComponent } from '@innogy/become-a-customer/shared';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import {
  energyAdvicePhoneRejectedFormControlID,
  sustainabilityAdvicePhoneRejectedFormControlID,
  type OrderConfirmationFormValues,
} from '@innogy/become-a-customer/state';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { type TextField } from '@sitecore-jss/sitecore-jss-angular';
import { SetValueAction } from 'ngrx-forms';

import { OrderFulfillmentStepComponent } from '../order-fulfillment-step.component';

interface DisclaimerLink {
  key: TextField;
  href: string;
  automationId: string;
}

@Component({
  selector: 'wl-order-fulfillment-confirmation[progressiveFormState]',
  templateUrl: './order-fulfillment-confirmation.component.html',
  styleUrls: [
    './order-fulfillment-confirmation.component.ed.scss',
    './order-fulfillment-confirmation.component.essent.scss',
  ],
})
export class OrderFulfillmentConfirmationComponent extends OrderFulfillmentStepComponent<OrderConfirmationFormValues> {
  private readonly modalService = inject(NgbModal);
  private readonly store$ = inject(Store);

  readonly offer = input<Offer>();

  readonly openAOMModal = output<Offer>();

  override readonly progressiveFormCardContent = computed(() => ({
    title: this.orderContent().checkCardTitle,
    subtitle: this.orderContent().checkCardSubtitle,
    submitButtonText: this.orderContent().checkCardSubmitButton,
    stepNumber: this.stepNumber(),
  }));

  readonly phoneConsentValues = computed(() => ({
    energyAdvice: this.formState().controls.energyAdvicePhoneRejected.value,
    sustainability:
      this.formState().controls.sustainabilityAdvicePhoneRejected.value,
  }));

  /**
   * Conditional dynamic rendering of the notification message
   * Preferable we don't build HTML manually but the info-card component does not support slots
   */
  readonly phoneConsentMessage = computed(() => {
    let message = this.orderContent().consentsMessageBody;
    message += '<br>';
    if (
      this.phoneConsentValues().energyAdvice ||
      this.phoneConsentValues().sustainability
    ) {
      message += '<ul>';
      if (this.phoneConsentValues().energyAdvice) {
        message += `<li>${this.orderContent().emailConsentsModalEnergyAdviceCheckbox}</li>`;
      }
      if (this.phoneConsentValues().sustainability) {
        message += `<li>${this.orderContent().emailConsentsModalSustainabilityCheckbox}</li>`;
      }
      message += '</ul>';
    }
    return message;
  });

  readonly disclaimers = computed((): DisclaimerLink[] => {
    const disclaimers: DisclaimerLink[] = [
      {
        key: { value: this.orderContent().generalTermsAndConditions },
        href: this.offer()?.generalTermsAndConditionsUrl ?? '',
        automationId:
          'order-fulfillment-check-conditions-general-terms-and-conditions',
      },
      {
        key: { value: this.orderContent().qualityCriteria },
        href: this.offer()?.qualityCriteriaUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-quality-criteria',
      },
      {
        key: { value: this.orderContent().productConditions },
        href: this.offer()?.productConditionsUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-product',
      },
    ];
    const actionConditionsUrl = this.offer()?.actionConditionsUrl;
    if (actionConditionsUrl) {
      disclaimers.push({
        key: { value: this.orderContent().campaignConditions },
        href: actionConditionsUrl,
        automationId: 'order-fulfillment-check-conditions-campaign',
      });
    }

    return disclaimers;
  });

  openAOMModalClick(e: Event) {
    const offer = this.offer();
    if (!offer) {
      throw new Error('No offer found to open AOM Modal with.');
    }
    e.preventDefault();
    this.openAOMModal.emit(offer);
  }

  async openConsentsModal() {
    const modal = this.modalService.open(PhoneConsentsModalComponent, {
      centered: true,
    });
    modal.componentInstance.rendering.set(this.rendering);
    modal.componentInstance.energyAdvicePhoneRejected.set(
      this.formState().value.energyAdvicePhoneRejected
    );
    modal.componentInstance.sustainabilityAdvicePhoneRejected.set(
      this.formState().value.sustainabilityAdvicePhoneRejected
    );
    const modalResult = await modal.result;

    if (modalResult && 'energyAdvicePhoneRejected' in modalResult) {
      this.store$.dispatch(
        new SetValueAction(
          energyAdvicePhoneRejectedFormControlID,
          modalResult.energyAdvicePhoneRejected
        )
      );
    }
    if (modalResult && 'sustainabilityAdvicePhoneRejected' in modalResult) {
      this.store$.dispatch(
        new SetValueAction(
          sustainabilityAdvicePhoneRejectedFormControlID,
          modalResult.sustainabilityAdvicePhoneRejected
        )
      );
    }
  }
}
